import { ProductsResponse } from "@/types/products-metadata"
import {
  BaseProduct,
  FindParams,
  StoreProduct,
  StoreProductReview,
} from "@/types/store"
import { fetch } from "@lib/util/fetch"
import { sortProducts } from "@lib/util/sort-products"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { cache } from "react"
import { getRegion } from "./regions"

const BACKEND_URL = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL

export const getProductsById = async function ({ id }: { id: string[] }) {
  try {
    const response = await fetch(`${BACKEND_URL}/store/products-by-id/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // next: { tags: ["products"] },
    })

    // Handle rate limiting gracefully
    if (response.status === 429) {
      console.warn("Rate limit detected in getProductsById, returning null")
      return null
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.statusText}`)
    }

    const { product } = await response.json()

    return product[0] || null
  } catch (error: any) {
    // Check if it's a rate limit error
    if (error?.isRateLimit || error?.status === 429) {
      console.warn("Rate limit error in getProductsById, returning null")
      return null
    }
    console.error("Error fetching product:", error)
    return null
  }
}

export type ProductWithReviewsResult = {
  product: StoreProduct | null
  reviews: StoreProductReview
}

function normalizeReview(
  apiReview: Record<string, unknown>
): StoreProductReview["data"][0] {
  const maskedName = String(
    apiReview.customer_name_masked ?? apiReview.customer_name ?? ""
  )
  const [firstName = "", lastName = ""] = maskedName.split(/\s+/, 2)
  return {
    id: Number(apiReview.id) ?? 0,
    customer_id: Number(apiReview.customer_id) ?? 0,
    customer_name: String(apiReview.customer_name ?? (firstName || maskedName)),
    customer_last_name: String(apiReview.customer_last_name ?? lastName),
    rating: Number(apiReview.rating) ?? 0,
    comment: String(apiReview.comment ?? ""),
    status: String(apiReview.status ?? ""),
    created_at: String(apiReview.created_at ?? ""),
    images: [],
    title: "",
    name: "",
    region_id: 0,
    variant_id: 0,
  }
}

export const getProductByHandle = async function (
  handle: string,
  regionId: string
): Promise<ProductWithReviewsResult> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/store/products-by-handle?handle=${handle}&region_id=${regionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.statusText}`)
    }

    const json = await response.json()
    const productPayload = json.product
    const reviewsList = Array.isArray(json.reviews) ? json.reviews : []

    const rawProduct =
      productPayload?.["0"] ??
      productPayload?.[0] ??
      (Array.isArray(productPayload) ? productPayload[0] : null)
    
    // If the API returns a structure like { "0": { ... } }, we extract it properly.
    const actualProduct = rawProduct && typeof rawProduct === 'object' && 'id' in rawProduct 
      ? rawProduct 
      : (productPayload?.["0"] ?? productPayload?.[0])
    
    const averageRating =
      typeof productPayload?.average_rating === "number"
        ? productPayload.average_rating
        : undefined
        
    const product: StoreProduct | null = actualProduct
      ? {
          ...actualProduct,
          ...(averageRating !== undefined && { average_rating: averageRating }),
        }
      : null

    const reviews: StoreProductReview = {
      metadata: {},
      data: reviewsList.map((r: Record<string, unknown>) => normalizeReview(r)),
    }

    return { product, reviews }
  } catch (error) {
    console.error("Error fetching product:", error)
    return {
      product: null,
      reviews: { metadata: {}, data: [] },
    }
  }
}

export const getProductsList = async function ({
  pageParam = 1,
  queryParams,
}: {
  pageParam?: number
  queryParams?: FindParams
}): Promise<{
  response: { products: StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: FindParams
}> {
  const limit = queryParams?.limit || 12
  const offset = pageParam * limit
  const region = await getRegion()

  if (!region) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }

  try {
    const response = await fetch(`${BACKEND_URL}/store/products`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    // Handle rate limiting gracefully
    if (response.status === 429) {
      console.warn(
        "Rate limit detected in getProductsList, returning empty data"
      )
      return {
        response: { products: [], count: 0 },
        nextPage: null,
      }
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.statusText}`)
    }

    const products = await response.json()
    return {
      response: {
        products: products.data || [],
        count: products.count || 0,
      },
      nextPage: products.count > offset + limit ? pageParam + 1 : null,
      queryParams,
    }
  } catch (error: any) {
    // Check if it's a rate limit error
    if (error?.isRateLimit || error?.status === 429) {
      console.warn("Rate limit error in getProductsList, returning empty data")
      return {
        response: { products: [], count: 0 },
        nextPage: null,
      }
    }
    console.error("Error fetching products:", error)
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }
}

/**
 * This will fetch 100 products to the Next.js cache and sort them based on the sortBy parameter.
 * It will then return the paginated products based on the page and limit parameters.
 */
export const getProductsListWithSort = async function ({
  page = 0,
  queryParams,
  sortBy = "created_at",
}: {
  page?: number
  queryParams?: FindParams
  sortBy?: SortOptions
}): Promise<{
  response: { products: BaseProduct[]; count: number }
  nextPage: number | null
  queryParams?: FindParams
  totalPages?: number
}> {
  const limit = queryParams?.limit || 12
  const pageNumber = Number.isInteger(page) ? page : 1

  // The default created_at sort is now supported server-side. Fetch only the
  // page we actually display (offset pagination) instead of a huge batch, so
  // the backend enriches far fewer products per request.
  if (sortBy === "created_at") {
    try {
      const url = new URL(`${BACKEND_URL}/store/products`)
      url.searchParams.set("page_size", String(limit))
      url.searchParams.set("page", String(pageNumber))
      url.searchParams.set("sort_by", "-product.created_at")

      const toArray = (value?: string | string[]): string[] =>
        Array.isArray(value) ? value : value ? [value] : []

      const filterSets: Array<[string, string[]]> = []
      const collectionIds = toArray(queryParams?.collection_id)
      if (collectionIds.length) {
        filterSets.push(["product.collection_id", collectionIds])
      }
      const categoryIds = toArray(queryParams?.category_id)
      if (categoryIds.length) {
        filterSets.push(["pcp.category_id", categoryIds])
      }
      const productIds = toArray(queryParams?.id)
      if (productIds.length) {
        filterSets.push(["product.id", productIds])
      }
      filterSets.forEach(([column, values], idx) => {
        url.searchParams.set(`filters[${idx}][column]`, column)
        url.searchParams.set(`filters[${idx}][operation]`, "in")
        values.forEach((value, j) => {
          url.searchParams.set(`filters[${idx}][values][${j}]`, value)
        })
      })

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        next: {
          revalidate: 1800,
          tags: ["products"],
        },
      })

      if (response.ok) {
        const body = await response.json()
        const products = body.data || body.products || []
        const count = body.count ?? body.total_count ?? products.length
        const totalPages =
          body.totalPages ?? (count > 0 ? Math.ceil(count / limit) : 1)
        const nextPage =
          pageNumber >= totalPages || body.count === 0 ? null : pageNumber + 1
        return {
          response: { products, count },
          nextPage,
          queryParams,
          totalPages,
        }
      }
    } catch (error) {
      // fall through to the client-side sort path
    }
  }

  const {
    response: { products, count },
  } = await getProductsList({
    pageParam: 0,
    queryParams: {
      ...queryParams,
      limit: 999,
    },
  })

  const sortedProducts = sortProducts(products, sortBy)

  const pageParam = (pageNumber - 1) * limit

  const nextPage = count > pageParam + limit ? pageParam + limit : null

  const paginatedProducts = sortedProducts.slice(pageParam, pageParam + limit)
  return {
    response: {
      products: paginatedProducts,
      count,
    },
    nextPage,
    queryParams,
  }
}

export const getProducts = async function (queryParams?: string) {
  try {
    const response = await fetch(
      `${BACKEND_URL}/store/products${queryParams ? `?${queryParams}` : ""}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        next: {
          revalidate: 300,
          tags: ["products"],
        },
      }
    )

    // Handle rate limiting gracefully
    if (response.status === 429) {
      console.warn("Rate limit detected in getProducts, returning null")
      return null
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.statusText}`)
    }

    const productList = await response.json()
    return productList.data || null
  } catch (error: any) {
    // Check if it's a rate limit error
    if (error?.isRateLimit || error?.status === 429) {
      console.warn("Rate limit error in getProducts, returning null")
      return null
    }
    console.error("Error fetching product:", error)
    return null
  }
}

/**
 * Cached function to fetch products by category ID.
 * Uses React cache() for request deduplication and Next.js fetch cache for revalidation.
 *
 * Uses `inCategoryTree` operation to get products from the category and all its child categories.
 * Limits initial load to 10 products for better performance.
 *
 * @param categoryId - The category ID to fetch products for
 * @param pageSize - Number of products to fetch (default: 10)
 * @param cursor - Optional cursor for pagination
 * @returns Products response with data and metadata, or null on error
 */
export const getProductsByCategoryId = cache(async function (
  categoryId: number | string,
  pageSize: number = 10,
  cursor?: string | null
): Promise<ProductsResponse<StoreProduct> | null> {
  try {
    // Use inCategoryTree to get products from category and all child categories
    const filters = `filters[0][column]=pcp.category_id&filters[0][operation]=inCategoryTree&filters[0][values][0]=${categoryId}`
    const pageSizeParam = `page_size=${pageSize}`
    const cursorParam = cursor
      ? `&cursor=${encodeURIComponent(cursor)}&direction=next`
      : ""
    const queryParams = `${pageSizeParam}&${filters}${cursorParam}`

    const response = await fetch(
      `${BACKEND_URL}/store/products?${queryParams}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        next: {
          revalidate: 1800,
          tags: ["products", `products-category-${categoryId}`], // Tag for on-demand revalidation
        },
      }
    )

    if (!response.ok) {
      throw new Error(
        `Failed to fetch products for category ${categoryId}: ${response.statusText}`
      )
    }

    const productList: ProductsResponse<StoreProduct> = await response.json()
    return productList || null
  } catch (error) {
    console.error(`Error fetching products for category ${categoryId}:`, error)
    return null
  }
})

/**
 * Helper function to get just the products array (for backward compatibility)
 */
export const getProductsByCategoryIdSimple = cache(async function (
  categoryId: number | string,
  pageSize: number = 10
): Promise<StoreProduct[] | null> {
  const result = await getProductsByCategoryId(categoryId, pageSize)
  return result?.data || null
})

export const getProductByVariantId = async function (ids: string[]) {
  try {
    const queryString = new URLSearchParams({
      variantIds: ids.join(","),
    }).toString()

    const response = await fetch(
      `${BACKEND_URL}/store/products-by-variant-id?${queryString}`, // Append query parameters to the URL
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(
        `Failed to fetch product by variant id: ${response.statusText}`
      )
    }
    const product = await response.json()
    return product.results || null
  } catch (error) {
    console.error("Error fetching product by variant id:", error)
    return null
  }
}

export const getProductsQuery = (queryParams?: string) => {
  const endpoint = `${BACKEND_URL}/store/products`

  return fetch(`${endpoint}${queryParams ? `?${queryParams}` : ""}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
}
